import { useState, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { Upload, X, Eye, ChevronLeft, Sparkles, BookOpen } from "lucide-react"
import { Button, Select, SelectItem, Card, CardBody, Divider } from "@heroui/react"
import { useStudentUploadPasco } from "@src/presentation/hooks/pasco"
import { useUniversities } from "@src/presentation/hooks/universities"
import { ToastService } from "@src/shared/services/toast.service"
import type { StudentUploadPascoData } from "@src/domain/pascos/value_objects/student_upload_pasco_data"

export default function CreatePascoPage() {
    const navigate = useNavigate()
    const [uploadedFile, setUploadedFile] = useState<File | null>(null)
    const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null)
    const [uploadProgress, setUploadProgress] = useState(0)
    const [isDragging, setIsDragging] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)

    // Default to empty string for university
    const [selectedUniversityId, setSelectedUniversityId] = useState<string>("")

    const { mutate: uploadPasco, isPending: isUploading } = useStudentUploadPasco({
        onSuccess: () => {
            ToastService.success("PASCO uploaded successfully!")
            navigate("/pascos")
        },
        onError: (error) => {
            ToastService.error(`Failed to upload PASCO: ${error.message}`)
        }
    })

    // Fetch universities for the dropdown
    const { data: universitiesData } = useUniversities()

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file) {
            processFile(file)
        }
    }

    const processFile = (file: File) => {
        // Validate file type
        if (file.type !== 'application/pdf') {
            ToastService.error("Please upload a PDF file")
            return
        }

        // Validate file size (50MB max)
        const maxSize = 50 * 1024 * 1024
        if (file.size > maxSize) {
            ToastService.error("File size must be less than 50MB")
            return
        }

        setUploadedFile(file)
        // Create a URL for the PDF preview
        const url = URL.createObjectURL(file)
        setFilePreviewUrl(url)
    }

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault()
        setIsDragging(true)
    }

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault()
        setIsDragging(false)
    }

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault()
        setIsDragging(false)

        const file = e.dataTransfer.files?.[0]
        if (file) {
            processFile(file)
        }
    }

    const handleRemoveFile = () => {
        // Revoke the object URL to free up memory
        if (filePreviewUrl) {
            URL.revokeObjectURL(filePreviewUrl)
        }
        setUploadedFile(null)
        setFilePreviewUrl(null)
    }

    // Clean up the object URL when component unmounts
    useEffect(() => {
        return () => {
            if (filePreviewUrl) {
                URL.revokeObjectURL(filePreviewUrl)
            }
        }
    }, [filePreviewUrl])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!uploadedFile) {
            ToastService.error("Please upload a PDF file")
            return
        }

        // Prevent double submission
        if (isUploading) {
            return
        }

        // Prepare the data according to StudentUploadPascoData interface
        const uploadData: StudentUploadPascoData = {
            file: uploadedFile,
            university_id: selectedUniversityId || undefined,
        }

        uploadPasco({
            data: uploadData,
            onUploadProgress: (progress: number) => setUploadProgress(progress)
        })
    }

    const isFormValid = uploadedFile

    return (
        <div className="h-full bg-background overflow-y-auto relative pb-16">
            {/* Header */}
            <header className="border-b border-border bg-background/80 backdrop-blur-xl supports-[backdrop-filter]:bg-background/60 sticky top-0 z-20 shadow-sm">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row h-auto sm:h-16 py-3 sm:py-0 items-start sm:items-center justify-between gap-3 sm:gap-0">
                        <div className="flex items-start sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
                            <Button
                                variant="light"
                                size="sm"
                                onPress={() => navigate("/pascos")}
                                startContent={<ChevronLeft className="h-4 w-4" />}
                                className="hover:scale-105 transition-transform"
                            >
                                Back
                            </Button>
                            <Divider orientation="vertical" className="h-6 hidden sm:block" />
                            <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-primary-600 shadow-md flex-shrink-0">
                                    <Upload className="h-4 sm:h-5 w-4 sm:w-5 text-primary-foreground" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <h1 className="text-sm sm:text-xl font-semibold text-foreground">
                                        Create New PASCO
                                    </h1>
                                    <p className="text-xs text-default-500">Upload past questions</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="container mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
                <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6 pb-8">
                    {/* Compact Info Banner */}
                    <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-primary/5 border border-primary/10">
                        <Sparkles className="h-4 w-4 text-primary flex-shrink-0" />
                        <p className="text-sm text-default-600">
                            <span className="font-medium text-foreground">Upload a PASCO</span> · Metadata will be extracted automatically using AI
                        </p>
                    </div>

                    {/* File Upload Section */}
                    <Card className="border border-border rounded shadow-none bg-card hover:shadow-custom transition-shadow">
                        <CardBody className="p-4 sm:p-6">
                            <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                                <BookOpen className="h-5 w-5" />
                                Upload PASCO File
                            </h2>

                            {!uploadedFile ? (
                                <div
                                    className={`rounded border-2 border-dashed ${isDragging ? 'border-primary bg-primary/5' : 'border-border bg-default-100'
                                        } p-8 sm:p-12 text-center transition-colors`}
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={handleDrop}
                                >
                                    <Upload className={`mx-auto h-12 w-12 ${isDragging ? 'text-primary' : 'text-default-400'}`} />
                                    <h3 className="mt-4 text-sm font-medium text-foreground">Drop your PDF here</h3>
                                    <p className="mt-2 text-sm text-default-500">
                                        Or click to browse
                                    </p>
                                    <label>
                                        <Button
                                            as="span"
                                            color="primary"
                                            startContent={<Upload className="h-4 w-4" />}
                                            className="mt-4 cursor-pointer rounded"
                                        >
                                            Choose PDF File
                                        </Button>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept=".pdf,application/pdf"
                                            onChange={handleFileChange}
                                            className="hidden"
                                        />
                                    </label>
                                    <p className="mt-2 text-xs text-default-400">PDF only · Up to 50MB</p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {/* File Info Card */}
                                    <Card className="border border-border rounded shadow-none">
                                        <CardBody className="p-4">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                                                        <svg className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                                                            />
                                                        </svg>
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-medium text-foreground">{uploadedFile.name}</p>
                                                        <p className="text-xs text-default-500">
                                                            {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                                                        </p>
                                                    </div>
                                                </div>
                                                <Button
                                                    isIconOnly
                                                    variant="bordered"
                                                    color="danger"
                                                    onPress={handleRemoveFile}
                                                    size="sm"
                                                >
                                                    <X className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </CardBody>
                                    </Card>

                                    {/* PDF Preview */}
                                    {filePreviewUrl && (
                                        <Card className="border-none p-0 shadow-none">
                                            <CardBody className="py-4">
                                                <div className="flex items-center gap-2 mb-3">
                                                    <Eye className="h-4 w-4 text-primary" />
                                                    <h3 className="text-sm font-semibold text-foreground">PDF Preview</h3>
                                                </div>
                                                <div className="rounded border border-default-200 overflow-hidden bg-default-50">
                                                    <iframe
                                                        src={`${filePreviewUrl}#view=FitH`}
                                                        className="w-full h-[500px] border-0"
                                                        title="PDF Preview"
                                                    />
                                                </div>
                                                <p className="mt-2 text-xs text-default-500 text-center">
                                                    Preview your uploaded PDF before submitting
                                                </p>
                                            </CardBody>
                                        </Card>
                                    )}
                                </div>
                            )}
                        </CardBody>
                    </Card>

                    {/* University Selection (Optional) */}
                    <Card className="border border-border rounded shadow-none bg-card hover:shadow-custom transition-shadow">
                        <CardBody className="p-4 sm:p-6">
                            <h2 className="text-lg font-semibold text-foreground mb-4">University Information</h2>

                            <div className="space-y-4">
                                <Select
                                    label="University (Optional)"
                                    name="universityId"
                                    selectedKeys={selectedUniversityId ? [selectedUniversityId] : []}
                                    onChange={(e) => setSelectedUniversityId(e.target.value)}
                                    placeholder="Select a university"
                                    description="You can optionally specify a university"
                                    classNames={{
                                        label: "text-foreground font-medium",
                                        trigger: 'rounded border border-border',
                                        popoverContent: 'rounded'
                                    }}
                                >
                                    {universitiesData?.universities?.map((university) => (
                                        <SelectItem key={university.university_id} className="rounded">
                                            {university.name}
                                        </SelectItem>
                                    )) || []}
                                </Select>

                                <div className="rounded bg-default-100 p-3">
                                    <p className="text-xs text-default-600">
                                        <strong>Note:</strong> The university field is optional. If you don't select one,
                                        the system will attempt to identify it from the PASCO content during processing.
                                    </p>
                                </div>
                            </div>
                        </CardBody>
                    </Card>

                    {/* Actions */}
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3 border-t border-divider pt-4 sm:pt-6">
                        <Button
                            variant="bordered"
                            onPress={() => navigate("/pascos")}
                            className="rounded w-full sm:w-auto"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            color="primary"
                            isDisabled={!isFormValid || isUploading}
                            isLoading={isUploading}
                            startContent={!isUploading ? <Upload className="h-4 w-4" /> : undefined}
                            className="rounded w-full sm:w-auto"
                        >
                            {isUploading ? `Uploading... ${uploadProgress}%` : "Create PASCO"}
                        </Button>
                    </div>
                </form>
            </main>
        </div>
    )
}
